import { Component } from './component.js';

/**
 * ComponentResolver - Isomorphic chain resolution via controller.parent
 *
 * Server and client configure dependencies at startup via static properties:
 *   ComponentResolver.Component = ServerComponent;  // class with resolve()
 *   ComponentResolver.getStore = () => storeObj;
 *   ComponentResolver.observer = observerInstance;
 *   ComponentResolver.config = configObj;
 */
export class ComponentResolver {
	// Injected by server/client at startup
	static Component = Component;
	static getStore = () => ({});
	static observer = { trigger: () => null };
	static config = {};

	/**
	 * Resolve the full parent chain for a component
	 * Returns array from root -> leaf with all components, data, and controllers
	 *
	 * @param {string} crate - Crate name (e.g., 'users' or 'users/admin')
	 * @param {string} componentName - Component name within crate (e.g., 'profile')
	 * @param {object} context - Request context (patterns, route, session, etc.)
	 * @returns {Array<{component: Component, data: object, controller: Controller}>}
	 */
	static async resolveChain(crate, componentName, context = {}) {
		const chain = [];
		let current = { crate, component: componentName };
		let isFirst = true;
		let level = 0;

		while (current) {
			let component = await this.Component.resolve(current.crate, current.component);
			let data = null;

			data = await component.executeProvider(context.patterns || {}, context.route || {});

			let ctrl = await component.instantiateController(data);

			// Apply config default parent for components without explicit controller
			// Skip if this component IS the $extends target (prevents infinite loop)
			if (!component.controller && !ctrl.parent && this.config?.public?.$extends) {
				const ext = this.config.public.$extends;
				const extParts = ext.split('/');
				const extComponent = extParts.pop();
				const extCrate = extParts.join('/');
				if (component.crate !== extCrate || component.component !== extComponent) {
					ctrl.parent = ext;
				}
			}

			// Observer hooks
			let guard = await this.observer.trigger('resolveComponent', {
				component,
				controller: ctrl,
				crate: component.crate,
				componentName: component.component,
				isFirst,
				level,
				chain,
				patterns: context.patterns || {},
				route: context.route || {},
				state: this.getStore()
			});

			// Only treat as guard if it contains $component/$data/$override
			if (guard === component || (guard && !guard.$component && !guard.$data && !guard.$override)) {
				guard = null;
			}

			// Controller middleware
			const localGuard = ctrl.middleware(this.getStore());
			if (localGuard) guard = localGuard;

			if (guard) {
				if (guard.$component) {
					const parts = guard.$component.split('/');
					const guardComponent = parts.pop();
					const guardCrate = parts.join('/');
					component = await this.Component.resolve(guardCrate, guardComponent);
					data = await component.executeProvider(context.patterns || {}, context.route || {});
					ctrl = await component.instantiateController(data);
				}

				if (guard.$data) {
					data = guard.$data;
					ctrl = await component.instantiateController(data);
				} else if (guard.$override) {
					const { $override, $component, ...rest } = guard;
					data = rest;
					ctrl = await component.instantiateController(data);
				}
			}

			chain.unshift({ component, data, controller: ctrl });

			let parentRef = ctrl.parent;
			isFirst = false;
			level++;

			if (parentRef) {
				const parts = parentRef.split('/');
				const parentComponent = parts.pop();
				const parentCrate = parts.join('/');
				current = { crate: parentCrate, component: parentComponent };
			} else {
				current = null;
			}
		}

		return chain;
	}

	/**
	 * Find the CSS bundle from the chain (first controller with css)
	 */
	static getCssFromChain(chain) {
		for (const { controller } of chain) {
			if (controller.css) {
				return controller.css;
			}
		}
		return null;
	}

	/**
	 * Prepare chain data for SPA response
	 */
	static async prepareResponse(chain) {
		const css = this.getCssFromChain(chain);
		const leaf = chain[chain.length - 1];

		const leafViewFn = await leaf.component.loadView();
		const view = leafViewFn ? leafViewFn({}) : null;

		return {
			chain: chain.map(({ component, data, controller }) => ({
				crate: component.crate,
				component: component.component,
				frontendHandler: component.frontendHandler,
				data,
				parent: controller.parent,
			})),
			css,
			view,
			data: leaf.data,
			frontendHandler: leaf.component.frontendHandler,
		};
	}

	/**
	 * Render the full parent chain into nested content
	 * Chain is [root, middle, ..., leaf] - render leaf first, inject into each parent
	 */
	static async renderChain(chain) {
		if (chain.length === 0) return null;

		let content = null;

		for (let i = chain.length - 1; i >= 0; i--) {
			const { component, data } = chain[i];
			const view = await component.loadView();

			if (!view) continue;

			const viewData = { ...data };
			if (content !== null) {
				viewData.content = content;
			}

			content = view(viewData);
		}

		return content;
	}
}

export default ComponentResolver;
